import React from 'react';
import { useHistory } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { Typography } from '@mui/material';
import JWTDecode from '../../utils/jwtDecode';
import { useQuery } from '../../utils/helpers';
import PreLoanRequestIndexV3 from '../../feature/preloan/v3';
import { getOriginPartner, patchOriginClick, patchOriginClickAuthenticated } from '../../queries/preloan/v3';
import LeadFormIndex from '../../components/LeadFormIndex';
import AlertDialog from '../../components/AlertDialog';
import type {
  Origin, Partner, PartnerEntityConfig, PreLoanRequestTokenType,
} from '../../components/LeadFormIndex/types';
import type { PatchOriginClickResponse } from '../../types/preloan/v3';
import analytics from '../../utils/analytics';
import NotFound from '../../navigation/404';

export default function PreLoanIndex() {
  const query = useQuery();
  const history = useHistory();
  const {
    boot: bootIntercom,
  } = useIntercom();
  const searchParams = React.useMemo(() => query, [query]);
  const [loading, setLoading] = React.useState(false);
  const [preLoanId, setPreLoanId] = React.useState('');
  const [showForm, setShowForm] = React.useState(false);
  const [notFound, setNotFound] = React.useState(false);
  const [error, setError] = React.useState(false);
  const defaultOriginClickRes = { originLabel: 'default', isActive: true };
  const defaultOriginPartner = {
    partnerName: 'Banca.Me',
    products: ['consumer', 'microLoan'] as ('consumer'|'microLoan'|'mortgage'|'downPayment')[],
    config: {
      styles: {
        primaryColor: '#755DDF',
        secondaryColor: '#755DDF',
        backgroundImage: 'https://www.banca.me/images/background.png',
        minimalLogo: 'https://www.banca.me/images/logo.png',
      },
    },
  };
  const [origin, setOrigin] = React.useState<Origin|undefined>(undefined);
  const [partner, setPartner] = React.useState<Partner|undefined>(undefined);
  const [showRedirectDialog, setShowRedirectDialog] = React.useState(false);

  const navigation = window.performance.getEntriesByType('navigation')[0].toJSON().type;

  const handleSetShowForm = (show: boolean) => {
    setShowForm(show);
  };

  const originClickAndPartner = async (params:{originIcomingData: string,
    click: boolean,
    token:string|null}) => {
    const { originIcomingData, click, token } = params;
    let originData: { originLabel: string, isActive: boolean } = defaultOriginClickRes;
    let partnerData: { partnerName: string, products: ('consumer'|'microLoan'|'mortgage'|'downPayment')[],
      config: PartnerEntityConfig } = defaultOriginPartner;
    try {
      setLoading(true);
      let res: PatchOriginClickResponse;
      if (token) {
        res = await patchOriginClickAuthenticated(originIcomingData, token);
      } else {
        res = await patchOriginClick(originIcomingData, click);
      }
      originData = res.data;
      if (!originData.isActive) {
        setNotFound(true);
      }
    } catch (e) {
      setShowRedirectDialog(true);
      return null;
    }
    try {
      const res = await getOriginPartner(originData.originLabel);
      partnerData = res.data;
    } catch (e) {
      setShowRedirectDialog(true);
      return null;
    }
    setLoading(false);
    searchParams.set('origin', originData.originLabel);
    searchParams.set('partner', partnerData.partnerName);

    if (token) {
      searchParams.set('token', token);
    }

    bootIntercom();
    history.push({
      search: searchParams.toString(),
    });
    setOrigin(originData);
    setPartner(partnerData);
    if (token) {
      setShowForm(true);
    }
    setLoading(false);
    return null;
  };

  React.useEffect(() => {
    const token = query.get('token');
    let originIcomingData = query.get('origin') || 'default';

    if (token) {
      setLoading(true);
      try {
        const payload: PreLoanRequestTokenType = JWTDecode(token);
        setPreLoanId(payload.preLoanRequestId);
        originIcomingData = payload.originLabel;
        analytics.identify(payload.identificationId, {
          plrId: payload.preLoanRequestId,
        });
      } catch (e) {
        setError(true);
      }
    }
    originClickAndPartner({ originIcomingData, click: navigation === 'navigate', token });
  }, [query.get('origin')]);

  if (notFound) {
    return <NotFound partnerData={partner ?? defaultOriginPartner} />;
  }

  const onCloseAvancesDialog = () => {
    analytics.page('PLR - ONBOARDING', 'REDIRECCION A SITIO WEB', { origin: origin?.originLabel });
    window.location.assign('https://banca.me');
  };

  const actionAvancesDialog = () => {
    analytics.page('PLR - ONBOARDING', 'REDIRECCION A PORTAL USUARIO', { origin: origin?.originLabel });
    history.push('/account');
  };

  return (
    <>
      <AlertDialog
        isTriggered={showRedirectDialog}
        dialogTitle={(
          <Typography variant="body1" fontWeight="bold" sx={{ paddingInline: 6.75 }} gutterBottom>
            ¿Quieres solicitar un avance con banca.me?
          </Typography>
        )}
        dialogContent={(
          <>
            <Typography variant="body2" sx={{ paddingInline: 1 }}>
              Si tienes una cuenta con nosotros y quieres solicitar un avance,
              {' '}
              <b>ingresa a tu Portal Usuario</b>
              {' '}
              y evalúate.
            </Typography>
            <br />
            <Typography
              variant="body2"
              sx={{ paddingInline: 1 }}
            >
              Si no tienes cuenta con banca.me, conoce todos
              {' '}
              <b>nuestros productos disponibles para ti</b>
              {' '}
              en nuestra página web.
            </Typography>
          </>
        )}
        submitButtonLabel="Ir a portal usuario"
        action={actionAvancesDialog}
        closeButtonLabel="Conocer más"
        onCloseAction={onCloseAvancesDialog}
        closeAfterSubmit
        maxWidth="xs"
        pageCall={{ category: 'PLR - ONBOARDING', name: 'REDIRECCION AVANCES', props: { origin: origin?.originLabel } }}
      />
      <LeadFormIndex
        partner={partner}
        error={error}
        loading={loading}
      >
        {!loading && origin
          ? (
            <PreLoanRequestIndexV3
              handleShowForm={handleSetShowForm}
              preLoanId={preLoanId}
              showForm={showForm}
              origin={origin}
              partner={partner ?? defaultOriginPartner}
            />
          ) : <div />}
      </LeadFormIndex>
    </>
  );
}
